// Compare current vs base so breaks can be tagged new vs already-there.
import { BrokenBlock, GeneratedLayout, Provenance, StudioItem } from "./studioTypes";

interface LaneEntry {
  node: string;
  xCm: number;
  widthCm: number;
}

const laneKey = (run: number, shelf: number) => `${run}|${shelf}`;

const scanBrokenBlocks = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  nodeOf: (item: StudioItem) => string,
): Map<string, { node: string; foreign: number; lanes: string[] }> => {
  const lanes = new Map<string, LaneEntry[]>();
  for (const item of items) {
    const placement = layout.placements[item.skuId];
    if (!placement) continue;
    const node = nodeOf(item);
    for (const segment of placement.segments) {
      const key = laneKey(segment.run, segment.shelf);
      const lane = lanes.get(key) ?? [];
      lane.push({ node, xCm: segment.xCm, widthCm: segment.hFacings * item.facingWidthCm });
      lanes.set(key, lane);
    }
  }
  const tally = new Map<string, { node: string; foreign: number; lanes: string[] }>();
  for (const [key, lane] of lanes) {
    lane.sort((a, b) => a.xCm - b.xCm);
    const firstIndex = new Map<string, number>();
    const lastIndex = new Map<string, number>();
    lane.forEach((entry, index) => {
      if (!firstIndex.has(entry.node)) firstIndex.set(entry.node, index);
      lastIndex.set(entry.node, index);
    });
    for (const [node, first] of firstIndex) {
      const last = lastIndex.get(node) ?? first;
      let foreign = 0;
      for (let i = first; i <= last; i++) {
        if (lane[i].node !== node) foreign += 1;
      }
      if (foreign > 0) {
        const entry = tally.get(node) ?? { node, foreign: 0, lanes: [] };
        entry.foreign += foreign;
        const [run, shelf] = key.split("|");
        entry.lanes.push(`aisle ${Number(run) + 1} shelf ${shelf}`);
        tally.set(node, entry);
      }
    }
  }
  return tally;
};

export const movedSkus = (
  base: GeneratedLayout,
  now: GeneratedLayout,
  xToleranceCm = 1,
): string[] => {
  const moved: string[] = [];
  for (const skuId of Object.keys(now.placements)) {
    const before = base.placements[skuId];
    const after = now.placements[skuId];
    if (!before) continue;
    const primaryBefore = before.segments.reduce((a, b) => (a.hFacings >= b.hFacings ? a : b));
    const primaryAfter = after.segments.reduce((a, b) => (a.hFacings >= b.hFacings ? a : b));
    if (
      primaryBefore.run !== primaryAfter.run ||
      primaryBefore.shelf !== primaryAfter.shelf ||
      Math.abs(primaryBefore.xCm - primaryAfter.xCm) > xToleranceCm
    ) {
      moved.push(skuId);
    }
  }
  return moved;
};

export const auditContiguity = (
  base: GeneratedLayout,
  now: GeneratedLayout,
  items: readonly StudioItem[],
  nodeOf: (item: StudioItem) => string,
): Provenance => {
  const baseTally = scanBrokenBlocks(base, items, nodeOf);
  const nowTally = scanBrokenBlocks(now, items, nodeOf);
  const broken: BrokenBlock[] = [...nowTally.values()]
    .map((entry) => ({
      node: entry.node,
      foreignFacings: entry.foreign,
      lanes: entry.lanes,
      introduced: !baseTally.has(entry.node),
    }))
    .sort(
      (a, b) => Number(b.introduced) - Number(a.introduced) || b.foreignFacings - a.foreignFacings,
    );
  return {
    broken,
    newBreaks: broken.filter((entry) => entry.introduced).length,
    preExistingBreaks: broken.filter((entry) => !entry.introduced).length,
    movedSkuIds: movedSkus(base, now),
  };
};
